import { StyleSheet, View } from 'react-native';

import { useLoyaltyPoints } from 'hooks/loyalty/use-loyalty-points';
import { NonNullableRewardCategory } from 'pages/loyalty/loyalty-rewards/types';
import { LoyaltyUser } from 'state/global-state/models/loyalty/user/user.types';
import { IEngineRewardsMap } from 'state/loyalty/types';
import { primitive } from 'styles/constants/primitives';

import { BottomLinks } from './components/bottom-links';
import { CrownIcon } from './components/crown-icon';
import { ExpirationWarning } from './components/expiration-warning';
import { useProgressCalculation } from './components/hooks/use-progress-calculation';
import { useTiers } from './components/hooks/use-tiers';
import { ProgressSection } from './components/progress-section';
import { RedeemCard } from './components/redeem-card';

interface ProfileCardProps {
  loyaltyUser: LoyaltyUser;
  engineRewardsMap: IEngineRewardsMap;
  rewardCategories: NonNullableRewardCategory[];
  lastSeenLoyaltyPoints: number;
}

export const ProfileCard = ({
  loyaltyUser,
  engineRewardsMap,
  rewardCategories,
  lastSeenLoyaltyPoints,
}: ProfileCardProps) => {
  const { name } = loyaltyUser;
  const userName = name.split(' ')[0];

  const { tiers } = useTiers({ engineRewardsMap, rewardCategories });
  const {
    userHasExpiringPoints,
    pointsExpirationDate,
    currentPoints,
    pointsToExpire,
  } = useLoyaltyPoints(loyaltyUser);

  const { progressData, animationState } = useProgressCalculation({
    currentPoints,
    lastSeenLoyaltyPoints,
    tiers,
  });

  return (
    <>
      <View style={styles.mainCard}>
        <CrownIcon />
        <View style={styles.progressContainer}>
          <ProgressSection
            userName={userName}
            tiers={tiers}
            currentPoints={currentPoints}
            lastSeenLoyaltyPoints={lastSeenLoyaltyPoints}
            progressData={progressData}
            animationState={animationState}
          />
        </View>

        {userHasExpiringPoints && (
          <ExpirationWarning
            pointsToExpire={pointsToExpire}
            expirationDate={pointsExpirationDate.raw}
          />
        )}
      </View>

      {/* Visible only if the user has reached the first tier */}
      {progressData.currentTier > 0 && <RedeemCard />}

      <BottomLinks />
    </>
  );
};

export const styles = StyleSheet.create({
  mainCard: {
    position: 'relative',
    borderRadius: 12,
    backgroundColor: primitive.$white,
  },
  progressContainer: {
    padding: primitive.spacing.$4,
  },
});
